import React, { useState } from 'react';
import useTempAdHistoryData from '../../hooks/useTempAdHistoryData';

// One cell value, defaulting to 0
const Cell = ({ value }) => (
    <td className="c15 cp"><div className="df jcc">{value ?? 0}</div></td>
);

// A table for one company-type bucket (Direct or Indirect)
function CompanyTable({ title, bucketData, modules, isDirectCompany }) {
    const [openModules, setOpenModules] = useState({});

    const toggle = (moduleId) =>
        setOpenModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));

    return (
        <div className="sectionWrapper p0-5rem fs0-75rem pr" style={{ width: '100%', overflowX: 'auto' }}>
            <div className="table-permission">
                <h2 className="tac mb1rem" style={{ fontSize: '1rem', fontWeight: 600 }}>
                    {title}
                </h2>
                <table
                    className={`table-userPermissions table-clientPermissions table-report temp-lead-matrix-table ${isDirectCompany ? 'temp-lead-matrix-table--direct' : 'temp-lead-matrix-table--indirect'}`}
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed',
                    }}
                >
                    <colgroup>
                        <col />
                        {[...Array(isDirectCompany ? 7 : 6)].map((_, i) => (
                            <col key={i} />
                        ))}
                    </colgroup>
                    <thead className="ps t0">
                        <tr>
                            <th className="index-table smft temp-lead-matrix-section-head">Section</th>
                            <th className="index-table taci smft" colSpan="3">Served</th>
                            <th className="index-table taci smft" colSpan="3">Clicked</th>
                            {isDirectCompany && (
                                <th className="index-table taci smft" colSpan="1">Ads</th>
                            )}
                        </tr>
                        <tr>
                            <th className="index-table smft"></th>
                            <th className="subHead smft tac">Week</th>
                            <th className="subHead smft tac">Month</th>
                            <th className="subHead smft tac">YTD</th>
                            <th className="subHead smft tac">Week</th>
                            <th className="subHead smft tac">Month</th>
                            <th className="subHead smft tac">YTD</th>
                            {isDirectCompany && <th className="subHead smft">(%)</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {modules.map((mod) => {
                            const isOpen = !!openModules[mod._id];
                            const hasSections = mod.sectionNos && mod.sectionNos.length > 0;

                            return (
                                <React.Fragment key={mod._id}>
                                    {/* Module header row — clickable accordion toggle */}
                                    <tr
                                        onClick={() => hasSections && toggle(mod._id)}
                                        style={{
                                            background: '#f0f2ff',
                                            cursor: hasSections ? 'pointer' : 'default',
                                            borderTop: '2px solid #DBDFEE',
                                        }}
                                    >
                                        <td
                                            colSpan={isDirectCompany ? 8 : 7}
                                            style={{ padding: '8px 12px' }}
                                        >
                                            <div className="df aic" style={{ gap: '8px' }}>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        width: '18px',
                                                        textAlign: 'center',
                                                        fontWeight: 700,
                                                        color: '#4A67FF',
                                                        fontSize: '0.9rem',
                                                    }}
                                                >
                                                    {hasSections ? (isOpen ? '▾' : '▸') : ''}
                                                </span>
                                                <span className="fw600 c15" style={{ fontSize: '0.9rem' }}>
                                                    {mod.moduleName}
                                                </span>
                                                {mod.moduleKey && (
                                                    <span
                                                        style={{
                                                            background: '#e9ecef',
                                                            borderRadius: '4px',
                                                            padding: '1px 6px',
                                                            fontSize: '0.72rem',
                                                            opacity: 0.85,
                                                        }}
                                                    >
                                                        {mod.moduleKey}
                                                    </span>
                                                )}
                                                <span style={{ fontSize: '0.72rem', color: '#888' }}>
                                                    ({mod.sectionNos?.length ?? 0} section{mod.sectionNos?.length !== 1 ? 's' : ''})
                                                </span>
                                            </div>
                                        </td>
                                    </tr>

                                    {/* Section rows — only shown when accordion is open */}
                                    {isOpen && mod.sectionNos.map((sectionNo) => {
                                        const s = bucketData[sectionNo];
                                        if (!s) return null;
                                        return (
                                            <tr key={sectionNo} style={{ borderBottom: '1px solid #F0F2FF' }}>
                                                <td
                                                    className="c15 temp-lead-matrix-section-cell"
                                                    style={{ paddingLeft: '32px', verticalAlign: 'top' }}
                                                >
                                                    <span className="fw600" style={{ marginRight: '4px' }}>{sectionNo}</span>
                                                    {s.sectionName && (
                                                        <span style={{ color: '#555', fontSize: '0.78rem' }}>
                                                            ({s.sectionName})
                                                        </span>
                                                    )}
                                                </td>
                                                <Cell value={s.served?.weekCount} />
                                                <Cell value={s.served?.monthServed} />
                                                <Cell value={s.served?.ytdCount} />
                                                <Cell value={s.clicked?.weekCount} />
                                                <Cell value={s.clicked?.monthServed} />
                                                <Cell value={s.clicked?.ytdCount} />
                                                {isDirectCompany && (
                                                    <Cell value={s.percentageOfTotalClients} />
                                                )}
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function TempLeadMatricReport() {
    const { data, loading } = useTempAdHistoryData();

    if (loading) {
        return (
            <div className="df jcc aic mt2rem" style={{ minHeight: '120px' }}>
                <span className="fs1rem c15">Loading report…</span>
            </div>
        );
    }

    if (!data || !data.modules || data.modules.length === 0) {
        return (
            <div className="df jcc aic mt2rem" style={{ minHeight: '120px' }}>
                <span className="fs1rem c15">No module data available.</span>
            </div>
        );
    }

    const { modules } = data;
    const directBucket = data['Direct Company'] || {};
    const indirectBucket = data['Indirect Company'] || {};

    return (
        <div className="df aifs jcsb" style={{ gap: '1rem', flexWrap: 'wrap' }}>
            <CompanyTable
                title="Existing Client Companies"
                bucketData={indirectBucket}
                modules={modules}
                isDirectCompany={true}
            />
            <CompanyTable
                title="Direct Companies"
                bucketData={directBucket}
                modules={modules}
                isDirectCompany={false}
            />
        </div>
    );
}
